import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { selectionProcessesAdmin } from '@/services/selectionProcesses';
import { PATHS } from '@/routes/paths';
import { Loader } from '@/components/custom/loader';
import StepIdentification from './StepIdentification';
import StepDeadline from './StepDeadline';
import StepQuestions from './StepQuestions';
import StepReview from './StepReview';

const validationSchema = Yup.object().shape({
  name: Yup.string().min(3, 'Mínimo 3 caracteres').required('Obrigatório'),
  description: Yup.string(),
  starts_at: Yup.date().required('Obrigatório').typeError('Data inválida'),
  ends_at: Yup.date().min(Yup.ref('starts_at'), 'Fim deve ser após o início').required('Obrigatório').typeError('Data inválida'),
  regional_id: Yup.number().nullable(),
  target_audience: Yup.string().oneOf(['student', 'teacher']).required('Obrigatório'),
  collect_education_info: Yup.boolean()
});

const empty = { name: '', description: '', starts_at: '', ends_at: '', regional_id: '', target_audience: 'student', collect_education_info: true };

export default function SelectionProcessContent({ id, currentStep, setCurrentStep, initialValues, loadingInitial }) {
  const navigate = useNavigate();
  const [localId, setLocalId] = useState(id || null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);

  const isEditing = Boolean(id);

  const formik = useFormik({
    initialValues: initialValues || empty,
    validationSchema,
    enableReinitialize: true,
    onSubmit: () => {}
  });

  useEffect(() => {
    if (initialValues) {
      formik.setValues(initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    setLocalId(id || null);
  }, [id]);

  const buildPayload = () => ({
    ...formik.values,
    regional_id: formik.values.regional_id || null
  });

  const ensureDraftSaved = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched({ name: true, description: true, starts_at: true, ends_at: true });
      toast.error('Corrija os erros antes de continuar.');
      return null;
    }

    if (localId) {
      try {
        await selectionProcessesAdmin.update(localId, buildPayload());
        return localId;
      } catch (err) {
        const apiErrors = err?.response?.data?.errors;
        const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || e.message || JSON.stringify(e)).join('; ') : err?.message || 'Erro ao salvar.';
        toast.error(message);
        return null;
      }
    }

    try {
      setAutoSaving(true);
      const res = await selectionProcessesAdmin.create(buildPayload());
      const newId = res.data?.data?.id ?? res.data?.id;
      if (!newId) throw new Error('Resposta inválida ao criar seletivo.');
      setLocalId(newId);
      toast.success('Rascunho salvo.');
      return newId;
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || e.message || JSON.stringify(e)).join('; ') : err?.message || 'Erro ao salvar.';
      toast.error(message);
      return null;
    } finally {
      setAutoSaving(false);
    }
  };

  const handleSave = async ({ publish = false } = {}) => {
    const savedId = await ensureDraftSaved();
    if (!savedId) return;

    if (publish) {
      try {
        await selectionProcessesAdmin.publish(savedId);
        toast.success('Seletivo publicado!');
      } catch (err) {
        const apiErrors = err?.response?.data?.errors;
        const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || e.message || JSON.stringify(e)).join('; ') : err?.message || 'Erro ao publicar.';
        toast.error(message);
        return;
      }
    } else if (!isEditing) {
      toast.success('Seletivo salvo como rascunho!');
    } else {
      toast.success('Seletivo atualizado com sucesso!');
    }

    navigate(PATHS.ADMIN_SELETIVO.DETAIL(savedId));
  };

  const handleDeadlineNext = async () => {
    const savedId = await ensureDraftSaved();
    if (savedId) setCurrentStep(3);
  };

  if (loadingInitial) return <Loader />;

  if (currentStep === 1) {
    return (
      <StepIdentification
        formik={formik}
        onNext={() => setCurrentStep(2)}
      />
    );
  }

  if (currentStep === 2) {
    return (
      <StepDeadline
        formik={formik}
        onBack={() => setCurrentStep(1)}
        onNext={handleDeadlineNext}
        submitting={autoSaving}
      />
    );
  }

  if (currentStep === 3) {
    return (
      <StepQuestions
        processId={localId}
        targetAudience={formik.values.target_audience}
        collectEducationInfo={formik.values.collect_education_info !== false}
        onBack={() => setCurrentStep(2)}
        onNext={() => setCurrentStep(4)}
        onCountChange={setQuestionCount}
      />
    );
  }

  return (
    <StepReview
      formik={formik}
      isEditing={isEditing || Boolean(localId)}
      submitting={formik.isSubmitting || autoSaving}
      questionCount={questionCount}
      onBack={() => setCurrentStep(3)}
      onSaveDraft={() => handleSave({ publish: false })}
      onPublish={() => handleSave({ publish: true })}
    />
  );
}
